/**
 * Хранилище браузера, которое не роняет страницу.
 *
 * В приватном окне Safari, при запрете cookies и в встроенных браузерах соцсетей
 * обращение к localStorage бросает исключение. Позиция в серии и скорость — удобство,
 * а не данные: без хранилища сайт обязан работать, просто ничего не помнить.
 */
export type StorageKind = "local" | "session";

function storage(kind: StorageKind): Storage | null {
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export function readItem(kind: StorageKind, key: string): string | null {
  try {
    return storage(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeItem(kind: StorageKind, key: string, value: string): void {
  try {
    storage(kind)?.setItem(key, value);
  } catch {
    // Переполнение или запрет: запомнить не вышло — и ладно.
  }
}

export function removeItem(kind: StorageKind, key: string): void {
  try {
    storage(kind)?.removeItem(key);
  } catch {
    // См. writeItem.
  }
}

/** JSON из хранилища с проверкой формы: чужие и старые записи не должны ломать код. */
export function readJson<T>(kind: StorageKind, key: string, guard: (value: unknown) => value is T): T | null {
  const raw = readItem(kind, key);
  if (raw === null) return null;
  try {
    const value: unknown = JSON.parse(raw);
    return guard(value) ? value : null;
  } catch {
    return null;
  }
}

export function writeJson(kind: StorageKind, key: string, value: unknown): void {
  try {
    writeItem(kind, key, JSON.stringify(value));
  } catch {
    // Циклы и BigInt сюда не попадают; на всякий случай молчим, как и writeItem.
  }
}
